import React, { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Checkbox,
  Link,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { ChatMessage } from "@webuild-ai/admin/models/components";

import { useChatStore } from "@/stores/ChatStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

export const RunPage: React.FC = () => {
  const { workflowId, runId } = useParams();
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [modifiedMessages, setModifiedMessages] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const { chat, load, updateMessageFeedback, updateMessageComment, submitFeedback } =
    useChatStore();
  const { adminClient } = useWebuildAPIClient();

  useEffect(() => {
    if (adminClient && runId) {
      load(adminClient, runId);
    }
  }, [adminClient, runId, load]);

  const handleFeedbackChange = useCallback(
    (index: number, newFeedback: string) => {
      const message = chat?.messages?.[index];

      if (message && message.id) {
        const liked = newFeedback === "positive" ? true : newFeedback === "negative" ? false : null;

        if (liked !== null) {
          updateMessageFeedback(message.id, liked);
          setModifiedMessages((prev) => new Set(prev).add(message.id));
        }
      }
    },
    [chat?.messages, updateMessageFeedback],
  );

  const handleCommentChange = useCallback(
    (index: number, newComment: string) => {
      const messageId = chat?.messages?.[index]?.id;

      if (messageId) {
        updateMessageComment(messageId, newComment);
        setModifiedMessages((prev) => new Set(prev).add(messageId));
      }
    },
    [chat?.messages, updateMessageComment],
  );

  const handleCheckboxChange = useCallback((index: number) => {
    setSelectedRows((prev) => {
      const newSelected = prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];

      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;

    setSelectAll(newSelectAll);
    setSelectedRows(newSelectAll ? chat?.messages?.map((_, i) => i) || [] : []);
  }, [selectAll, chat?.messages]);

  const handleCreateDataset = useCallback(() => {
    const selectedMessages =
      chat?.messages?.filter((_, index) => selectedRows.includes(index)) || [];
    const datasetData = selectedMessages.map((message) => ({
      ...message,
      comment: message.feedback || "",
    }));

    logger.info("Creating dataset", { itemCount: String(datasetData.length) });
    // Add your dataset creation logic here
  }, [chat?.messages, selectedRows]);

  const handleSaveChanges = useCallback(async () => {
    if (!adminClient || !runId || modifiedMessages.size === 0) return;

    try {
      setIsSaving(true);
      const savePromises = Array.from(modifiedMessages).map(async (messageId) => {
        const message = chat?.messages?.find((msg) => msg.id === messageId);

        if (message) {
          await submitFeedback(
            adminClient,
            runId,
            messageId,
            message.liked || false,
            message.feedback || "",
          );
        }
      });

      await Promise.all(savePromises);
      setModifiedMessages(new Set());
      logger.info("All changes saved successfully");
      setSaveStatus({ type: "success", message: "All changes saved successfully" });
      setTimeout(() => setSaveStatus({ type: null, message: "" }), 3000);
    } catch (error) {
      logger.error("Error saving changes", error);
      setSaveStatus({ type: "error", message: "Error saving changes" });
      setTimeout(() => setSaveStatus({ type: null, message: "" }), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [adminClient, runId, modifiedMessages, chat?.messages, submitFeedback]);

  const getFeedbackColor = useCallback((feedback: string) => {
    switch (feedback) {
      case "positive":
        return "text-green-600";
      case "negative":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  }, []);

  const formatTime = useCallback((timestamp: string | undefined) => {
    if (!timestamp) return "";

    return new Date(timestamp).toLocaleTimeString();
  }, []);

  const getRoleIcon = useCallback((role: string) => {
    return role === "user" ? "👤" : "🤖";
  }, []);

  const getFeedbackText = useCallback((liked: boolean | null | undefined) => {
    if (liked === null || liked === undefined) return "none";

    return liked ? "positive" : "negative";
  }, []);

  if (!chat) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-2 text-gray-600">{t("Loading chat data...")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">
        {workflowId} - {runId}
      </h1>
      <div className="rounded-lg shadow p-4">
        <div className="flex justify-between items-center mb-4">
          <Link
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            href={`/workflows/${workflowId}/metrics`}
          >
            <span>{t("View Workflow Metrics")}</span>
            <svg
              className="h-5 w-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clipRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                fillRule="evenodd"
              />
            </svg>
          </Link>
        </div>
        <span className="block text-sm text-gray-500">
          {t("User can review the human feedback, reclassify it and create a dataset from it")}
        </span>

        <div className="mt-4">
          <div className="flex gap-2 mb-4">
            <Button isDisabled={selectedRows.length === 0} onPress={handleCreateDataset}>
              {t("Create Dataset")}
            </Button>
            {modifiedMessages.size > 0 && (
              <Button
                className="btn-brand-primary"
                isDisabled={isSaving}
                isLoading={isSaving}
                onPress={handleSaveChanges}
              >
                {t("Save Changes")} ({modifiedMessages.size})
              </Button>
            )}
          </div>

          <Table aria-label="Logs table">
            <TableHeader>
              <TableColumn>
                <Checkbox isSelected={selectAll} onValueChange={handleSelectAll} />
              </TableColumn>
              <TableColumn>{t("Time")}</TableColumn>
              <TableColumn>{t("Role")}</TableColumn>
              <TableColumn>{t("Message")}</TableColumn>
              <TableColumn>{t("Feedback")}</TableColumn>
              <TableColumn>{t("Comment")}</TableColumn>
              <TableColumn>{t("Status")}</TableColumn>
            </TableHeader>
            <TableBody>
              {chat.messages?.map((message: ChatMessage, index: number) => (
                <TableRow key={message.id || index}>
                  <TableCell>
                    <Checkbox
                      isSelected={selectedRows.includes(index)}
                      onValueChange={() => handleCheckboxChange(index)}
                    />
                  </TableCell>
                  <TableCell>{formatTime(message.timestamp)}</TableCell>
                  <TableCell>{getRoleIcon(message.role)}</TableCell>
                  <TableCell>
                    <div className="max-w-md truncate">
                      {typeof message.content === "string"
                        ? message.content
                        : JSON.stringify(message.content)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {message.role === "assistant" ? (
                      <select
                        className={`${getFeedbackColor(getFeedbackText(message.liked))} border rounded p-1`}
                        value={getFeedbackText(message.liked)}
                        onChange={(e) => handleFeedbackChange(index, e.target.value)}
                      >
                        <option className="text-green-600" value="positive">
                          Positive
                        </option>
                        <option className="text-red-600" value="negative">
                          Negative
                        </option>
                        <option className="text-gray-600" value="none">
                          None
                        </option>
                      </select>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {message.role === "assistant" ? (
                      <input
                        className="border rounded p-1 w-full"
                        placeholder="Add a comment..."
                        type="text"
                        value={message.feedback || ""}
                        onChange={(e) => handleCommentChange(index, e.target.value)}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {modifiedMessages.has(message.id) ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Modified
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Unmodified</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      {saveStatus.type && (
        <div className="mt-4 p-4 bg-gray-100 rounded-lg">
          {saveStatus.type === "success" ? (
            <p className="text-green-600">{saveStatus.message}</p>
          ) : (
            <p className="text-red-600">{saveStatus.message}</p>
          )}
        </div>
      )}
    </div>
  );
};
